/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useState } from 'react';
import {
  flexRender,
  type Header,
  type Table as TanstackTable
} from '@tanstack/react-table';

import {
  Card,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SkeletonTable,
  ScrollArea,
  ScrollBar,
  cn,
  isSafari
} from '@yakit/ui-components';

import { Maximize, Minimize } from 'lucide-react';

export interface IBaseTableProps<T> {
  data: T[];
  table: TanstackTable<T>;
  fullWidth?: boolean;
}

const Resizer = <T,>({
  table,
  header
}: {
  table: TanstackTable<T>;
  header: Header<T, any>;
}) => {
  return (
    <div
      onDoubleClick={() => header.column.resetSize()}
      onMouseDown={header.getResizeHandler()}
      className={cn(
        'bg-secondary absolute top-0 h-full w-0 cursor-col-resize touch-none transition-all select-none group-hover:w-1',
        {
          'bg-primary': header.column.getIsResizing(),
          'right-0': table.options.columnResizeDirection === 'ltr',
          'left-0': table.options.columnResizeDirection === 'rtl'
        }
      )}
      style={{
        transform: header.column.getIsResizing()
          ? `translateX(${
              (table.options.columnResizeDirection === 'rtl' ? -1 : 1) *
              (table.getState().columnSizingInfo.deltaOffset ?? 0)
            })px`
          : ''
      }}
    />
  );
};

export const VDLBaseTable = <T,>({
  data,
  table,
  fullWidth
}: IBaseTableProps<T>) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      className={cn('relative flex-1 overflow-hidden py-0', {
        'fixed inset-0 h-full w-full rounded-none': expanded
      })}
    >
      <Button
        className="bg-primary-foreground/60 absolute right-4 bottom-4 z-20 h-8 w-8 shadow-md"
        size="icon"
        variant="outline"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <Minimize className="h-4 w-4" />
        ) : (
          <Maximize className="h-4 w-4" />
        )}
      </Button>
      <div className="absolute h-full w-full">
        <ScrollArea className="h-full">
          {data.length > 0 ? (
            <Table
              className="border-separate border-spacing-0"
              style={fullWidth ? undefined : { width: table.getTotalSize() }}
            >
              <TableHeader
                className={cn(
                  'sticky top-0 z-10 shadow-xs',
                  isSafari() ? 'relative' : 'sticky'
                )}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const columnRelativeDepth =
                        header.depth - header.column.depth;

                      if (
                        !header.isPlaceholder &&
                        columnRelativeDepth > 1 &&
                        header.id === header.column.id
                      ) {
                        return null;
                      }

                      let rowSpan = 1;
                      if (header.isPlaceholder) {
                        const leafs = header.getLeafHeaders();
                        const lastLeafDepth =
                          leafs?.[leafs.length - 1]?.depth ?? header.depth;
                        rowSpan = lastLeafDepth - header.depth;
                      }

                      return (
                        <TableHead
                          key={header.id}
                          style={{ width: header.column.getSize() }}
                          className="group bg-opacity-55 relative border-r border-b bg-slate-100 backdrop-blur-md backdrop-filter"
                          colSpan={header.colSpan}
                          rowSpan={rowSpan}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanResize() && (
                            <Resizer table={table} header={header} />
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <MemoizedTableBodyComponent table={table} data={data} />
            </Table>
          ) : (
            <SkeletonTable />
          )}
          <ScrollBar orientation="horizontal" />
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </div>
    </Card>
  );
};

interface ITableBodyComponent<T> {
  table: TanstackTable<T>;
  data: T[];
}

const TableBodyComponent = <T,>({ table }: ITableBodyComponent<T>) => {
  return (
    <TableBody>
      {table.getRowModel().rows.map((row) => {
        return (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => {
              return (
                <TableCell
                  key={cell.id}
                  className="border-r border-b whitespace-normal"
                  style={{ width: cell.column.getSize() }}
                >
                  <div className="flex">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

const MemoizedTableBodyComponent = memo(TableBodyComponent, (prev, next) => {
  return (
    prev.table !== next.table &&
    prev.table.options.data !== next.table.options.data &&
    prev.table.options.state.expanded !== next.table.options.state.expanded &&
    prev.table.options.state.rowSelection !==
      next.table.options.state.rowSelection &&
    prev.table.options.columns !== next.table.options.columns &&
    prev.table.options.state.sorting !== next.table.options.state.sorting
  );
}) as typeof TableBodyComponent;
